import { useMemo } from 'react'

import {
  useAuthenticated,
  useAuthStatusSuspense,
} from '@jotta/auth-client/useAuthStatus'
import type { GetConfigRequest } from '@jotta/grpc-connect-openapi/config'
import { getConfig as getConfigDescriptor } from '@jotta/grpc-connect-openapi/configQuery'
import { getCurrentLanguage } from '@jotta/i18n'
import type { PartialMessage } from '@jotta/types/compat'
import { FeatureToggle } from '@jotta/types/FeatureToggle'

import { createConnectQuery } from '../createConnectQuery'
import { authTransport, publicTransport } from '../transport'

import { useBrandCode } from './BrandCodeProvider'
import { emptyConfigResponse } from './emptyConfigResponse'

const useConfigQuery = createConnectQuery(getConfigDescriptor)

function makeRequest(
  customerGroupOverride?: string,
  brandCode?: string,
): PartialMessage<GetConfigRequest> {
  return {
    hostname: window.location.hostname,
    featureToggleIds: Object.values(FeatureToggle),
    preferredLanguage: getCurrentLanguage(),
    customerGroupCode: customerGroupOverride,
    brandCode: brandCode,
  }
}
export async function getConfig() {
  return (
    (await useConfigQuery.fetch(makeRequest(), {
      transport: authTransport,
    })) || emptyConfigResponse
  )
}

export function useGetConfig(customerGroupOverride?: string) {
  const brandCode = useBrandCode()
  const authenticated = useAuthenticated()
  const request = makeRequest(customerGroupOverride, brandCode)

  const q = useConfigQuery({
    variables: request,
    refetchOnMount: false,
    enabled: authenticated !== 'UNKNOWN',
    transport: authenticated === 'TRUE' ? authTransport : publicTransport,
    queryKeyHashFn: queryKey => JSON.stringify([...queryKey, authenticated]),
  })

  return useMemo(
    () => ({
      ...q,
      data: q.data,
    }),
    [q],
  )
}

export function useGetConfigSuspense(customerGroupOverride?: string) {
  const brandCode = useBrandCode()
  const authenticated = useAuthStatusSuspense().data.authenticated
  return useConfigQuery.suspense(
    makeRequest(customerGroupOverride, brandCode),
    {
      refetchOnMount: false,
      transport: authenticated ? authTransport : publicTransport,
      queryKeyHashFn: queryKey => JSON.stringify([...queryKey, authenticated]),
    },
  )
}
